import { Link, Tooltip, Typography } from "@hellocarbo/lasagne";
import { DownloadIcon } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { PortalContainerContext } from "@/contexts";
import { TImportDeposit } from "@/types";

type FileInformationProps = {
  importDeposit: TImportDeposit;
};

function FileInformation({ importDeposit }: FileInformationProps) {
  const { t } = useTranslation("importDeposit");
  const { container } = useContext(PortalContainerContext);

  return (
    <div className="p-md border-grayscale-xx-light space-y-xs float-right max-w-[40%] flex-1 items-center justify-center rounded-lg border">
      <Typography as="h2" step="1" weight="bold">
        {t("importDeposit:importDetails.informations.title")}
      </Typography>

      <div className="gap-y-2xs grid grid-cols-[auto,1fr] grid-rows-4">
        <Typography
          step="-1"
          weight="semibold"
          className="col-start-1 row-start-1"
        >
          {t("importDeposit:importDetails.informations.file")}
        </Typography>
        <p className="col-start-2 row-start-1 text-right">
          <Tooltip
            content={t("importDeposit:importDetails.informations.downloadFile")}
            side="left"
            portalContainer={container as Element}
          >
            <Link href={importDeposit.download_link} className="flex">
              <DownloadIcon
                size={16}
                className="text-secondary-light min-w-sm"
              />
              <Typography
                step="-1"
                weight="semibold"
                className="text-secondary-light text-right"
              >
                {importDeposit.input_file}
              </Typography>
            </Link>
          </Tooltip>
        </p>

        <Typography
          step="-1"
          weight="semibold"
          className="col-start-1 row-start-2"
        >
          {t("importDeposit:importDetails.informations.dataType")}
        </Typography>
        <Typography
          step="-1"
          className="text-grayscale col-start-2 row-start-2 text-right"
        >
          {t(`importDeposit:importTypeList.${importDeposit.import_type}`)}
        </Typography>

        <Typography
          step="-1"
          weight="semibold"
          className="col-start-1 row-start-3"
        >
          {t("importDeposit:importDetails.informations.importDate")}
        </Typography>

        <Typography
          step="-1"
          className="text-grayscale col-start-2 row-start-3 text-right"
        >
          {t("formatter:date", {
            value: new Date(importDeposit.created_at),
          })}
        </Typography>

        {importDeposit.status !== "refused" && importDeposit.total_rows > 0 && (
          <>
            <Typography
              step="-1"
              weight="semibold"
              className="col-start-1 row-start-4"
            >
              {t("importDeposit:importDetails.informations.lineNumber")}
            </Typography>
            <Typography
              step="-1"
              className="text-grayscale col-start-2 row-start-4 text-right"
            >
              {t("formatter:number", { value: importDeposit.total_rows })}
            </Typography>
          </>
        )}
      </div>
    </div>
  );
}

export default FileInformation;
